const buildLegacyItemProvenanceUpdates = ({
  char,
  item,
  ability = null,
  buffs,
  debuffs,
  addedBy,
  now,
  sourceLabel = item.name,
}) => {
  if (!buffs.length && !debuffs.length) {
    return {};
  }

  const nextBuffList = [...(char.buffList || []), ...buffs];
  const nextDebuffList = [...(char.debuffList || []), ...debuffs];
  const nextBuffData = { ...(char.buffData || {}) };
  const nextBuffSources = { ...(char.buffSources || {}) };
  const permBuffs = char.permanentBuffs || [];
  const permDebuffs = char.permanentDebuffs || [];
  const updates = {};
  const itemDefinitionId = item.definitionId || item.itemId || null;
  const provenance = {
    sourceType: 'item',
    itemEntryId: item.id,
    itemId: item.itemId || item.id,
    itemDefinitionId,
    itemName: item.name || null,
    mode: addedBy,
    abilityId: ability?.id || null,
    abilityName: ability?.name || null,
  };

  buffs.forEach((buff) => {
    const occurrence =
      (char.buffList || []).filter((value) => value === buff).length +
      permBuffs.filter((value) => value === buff).length;
    const key = occurrence === 0 ? buff : `${buff}::${occurrence}`;

    nextBuffData[key] = {
      source: sourceLabel,
      sourceItemId: item.id,
      sourceItemEntryId: item.id,
      sourceItemDefinitionId: itemDefinitionId,
      sourceAbilityId: ability?.id || null,
      sourceAbilityName: ability?.name || null,
      addedAt: now,
      addedBy,
      noteVisible: true,
      provenance,
    };

    if (nextBuffSources[buff] === undefined) {
      nextBuffSources[buff] = sourceLabel;
    }
  });

  debuffs.forEach((debuff) => {
    const occurrence =
      (char.debuffList || []).filter((value) => value === debuff).length +
      permDebuffs.filter((value) => value === debuff).length;
    const key = occurrence === 0 ? debuff : `${debuff}::${occurrence}`;

    nextBuffData[key] = {
      source: sourceLabel,
      sourceItemId: item.id,
      sourceItemEntryId: item.id,
      sourceItemDefinitionId: itemDefinitionId,
      sourceAbilityId: ability?.id || null,
      sourceAbilityName: ability?.name || null,
      addedAt: now,
      addedBy,
      noteVisible: true,
      provenance,
    };

    if (nextBuffSources[debuff] === undefined) {
      nextBuffSources[debuff] = sourceLabel;
    }
  });

  if (buffs.length > 0) {
    updates.buffList = nextBuffList;
  }
  if (debuffs.length > 0) {
    updates.debuffList = nextDebuffList;
  }
  updates.buffData = nextBuffData;
  updates.buffSources = nextBuffSources;

  return updates;
};

const parseLegacyEffectInstanceName = (value) => {
  const text = String(value || '');
  const separatorIndex = text.lastIndexOf('::');
  if (separatorIndex < 0) return text;

  const suffix = Number.parseInt(text.slice(separatorIndex + 2), 10);
  if (!Number.isFinite(suffix)) return text;
  return text.slice(0, separatorIndex);
};

const normalizeItemSourceName = (value) => String(value || '').trim().toLowerCase();

const buildLegacyEffectKeyForOccurrence = (text, occurrence) => (
  occurrence === 0 ? text : `${text}::${occurrence}`
);

const getLegacyEffectKeyOccurrence = (key) => {
  const text = String(key || '');
  const separatorIndex = text.lastIndexOf('::');
  if (separatorIndex < 0) return 0;
  const suffix = Number.parseInt(text.slice(separatorIndex + 2), 10);
  return Number.isFinite(suffix) ? suffix : 0;
};

const isItemAbilityEffectData = (effectData = {}) => {
  if (!effectData || typeof effectData !== 'object') return false;
  const provenance = effectData.provenance && typeof effectData.provenance === 'object'
    ? effectData.provenance
    : {};
  const mode = String(effectData.sourceMode || effectData.addedBy || provenance.mode || '').toLowerCase();
  return mode === 'item-ability' || !!(effectData.sourceAbilityId || provenance.abilityId);
};

const isDurableEffectInstanceItemAbilityData = (effectData = {}) => (
  !!effectData?.effectInstanceId && isItemAbilityEffectData(effectData)
);

const shouldKeepItemEffectLifecycleKey = ({
  entry,
  itemEffects = [],
  buffData = {},
}) => {
  const text = parseLegacyEffectInstanceName(entry);
  if (!itemEffects.includes(text)) return true;
  return isDurableEffectInstanceItemAbilityData(buffData?.[entry]);
};

const remapLegacyEffectKeyAfterOccurrenceRemoval = (key, text, removedOccurrence) => {
  if (parseLegacyEffectInstanceName(key) !== text) return key;
  const occurrence = getLegacyEffectKeyOccurrence(key);
  if (occurrence === removedOccurrence) return null;
  if (occurrence > removedOccurrence) {
    return buildLegacyEffectKeyForOccurrence(text, occurrence - 1);
  }
  return key;
};

const remapLegacyEffectKeysAfterOccurrenceRemovals = (entries = [], removals = []) => (
  (entries || [])
    .map((entry) => removals.reduce(
      (current, removal) => (
        current == null
          ? null
          : remapLegacyEffectKeyAfterOccurrenceRemoval(current, removal.text, removal.removedOccurrence)
      ),
      entry,
    ))
    .filter(Boolean)
);

const listLegacyEffectKeys = (text, permanentList = [], transientList = []) => {
  const keys = [];
  let occurrence = 0;

  [...(permanentList || []), ...(transientList || [])].forEach((entry) => {
    if (entry !== text) return;
    keys.push(buildLegacyEffectKeyForOccurrence(text, occurrence));
    occurrence += 1;
  });

  return keys;
};

const countLegacyText = (text, ...lists) => lists.reduce(
  (total, list) => total + (list || []).filter((entry) => entry === text).length,
  0,
);

const findTransientIndexForGlobalOccurrence = (text, list, priorLists, occurrence) => {
  const priorCount = countLegacyText(text, ...(priorLists || []));
  const transientOccurrence = occurrence - priorCount;
  if (transientOccurrence < 0) return -1;

  let seen = 0;
  for (let index = 0; index < list.length; index += 1) {
    if (list[index] !== text) continue;
    if (seen === transientOccurrence) return index;
    seen += 1;
  }
  return -1;
};

const reindexLegacyEffectDataInPlace = (buffData, text, totalBefore, removedOccurrence) => {
  const metadataByOccurrence = [];
  for (let occurrence = 0; occurrence < totalBefore; occurrence += 1) {
    const key = buildLegacyEffectKeyForOccurrence(text, occurrence);
    metadataByOccurrence.push(buffData[key]);
    delete buffData[key];
  }

  metadataByOccurrence.splice(removedOccurrence, 1);
  metadataByOccurrence.forEach((metadata, occurrence) => {
    if (metadata === undefined) return;
    buffData[buildLegacyEffectKeyForOccurrence(text, occurrence)] = metadata;
  });
};

const legacyEffectDataMatchesItem = (effectData = {}, item = {}, fallbackSource = null) => {
  const provenance = effectData.provenance && typeof effectData.provenance === 'object'
    ? effectData.provenance
    : {};
  const itemIds = new Set([
    item.id,
    item.itemId,
    item.definitionId,
  ].filter(Boolean).map(String));

  const effectItemIds = [
    effectData.sourceItemEntryId,
    effectData.sourceItemId,
    effectData.sourceItemDefinitionId,
    provenance.itemEntryId,
    provenance.itemId,
    provenance.itemDefinitionId,
    provenance.definitionId,
  ].filter(Boolean).map(String);

  if (effectItemIds.some((id) => itemIds.has(id))) return true;

  const itemName = normalizeItemSourceName(item.name);
  if (!itemName) return false;

  return [
    effectData.source,
    effectData.itemName,
    provenance.itemName,
    fallbackSource,
  ].some((source) => normalizeItemSourceName(source) === itemName);
};

const hasLegacyEffectFromItem = ({
  text,
  transientList,
  permanentList,
  buffData,
  sourceMap,
  item,
}) => {
  const keys = listLegacyEffectKeys(text, permanentList, transientList);
  return keys.some((key) => {
    const effectData = buffData?.[key] || {};
    if (isItemAbilityEffectData(effectData)) return false;
    return legacyEffectDataMatchesItem(
      effectData,
      item,
      sourceMap?.[text] || null,
    );
  });
};

const buildLegacyEquipProvenanceUpdates = ({ char, item, now }) => {
  const removedItemBuffs = char.removedItemBuffs || {};
  const itemRemovedBuffs = removedItemBuffs[item.id] || [];
  const shouldRestoreOnEquip = item.restoreRemovedOnEquip !== false;
  const updates = {};

  const buffsToAdd = (item.buffs || []).filter((buff) => {
    if (!shouldRestoreOnEquip && itemRemovedBuffs.includes(buff)) return false;
    return true;
  });
  const debuffsToAdd = (item.debuffs || []).filter((debuff) => {
    if (!shouldRestoreOnEquip && itemRemovedBuffs.includes(debuff)) return false;
    return true;
  });

  if (!item.buffsApplyOnUse && (buffsToAdd.length > 0 || debuffsToAdd.length > 0)) {
    const nextBuffList = [...(char.buffList || [])];
    const nextDebuffList = [...(char.debuffList || [])];
    const nextBuffData = { ...(char.buffData || {}) };
    const nextBuffSources = { ...(char.buffSources || {}) };
    const nextPermBuffs = char.permanentBuffs || [];
    const nextPermDebuffs = char.permanentDebuffs || [];
    const itemDefinitionId = item.definitionId || item.itemId || null;
    const provenance = {
      sourceType: 'item',
      itemEntryId: item.id,
      itemId: item.itemId || item.id,
      itemDefinitionId,
      itemName: item.name || null,
      mode: 'item-equip',
      abilityId: null,
      abilityName: null,
    };

    buffsToAdd.forEach((buff) => {
      if (hasLegacyEffectFromItem({
        text: buff,
        transientList: nextBuffList,
        permanentList: nextPermBuffs,
        buffData: nextBuffData,
        sourceMap: nextBuffSources,
        item,
      })) {
        return;
      }

      const occurrence =
        nextBuffList.filter((value) => value === buff).length +
        nextPermBuffs.filter((value) => value === buff).length;
      const key = occurrence === 0 ? buff : `${buff}::${occurrence}`;

      nextBuffList.push(buff);
      nextBuffData[key] = {
        source: item.name,
        sourceItemId: item.id,
        sourceItemEntryId: item.id,
        sourceItemDefinitionId: itemDefinitionId,
        addedAt: now,
        addedBy: 'item-equip',
        noteVisible: true,
        provenance,
      };
      if (nextBuffSources[buff] === undefined) {
        nextBuffSources[buff] = item.name;
      }
    });

    debuffsToAdd.forEach((debuff) => {
      if (hasLegacyEffectFromItem({
        text: debuff,
        transientList: nextDebuffList,
        permanentList: nextPermDebuffs,
        buffData: nextBuffData,
        sourceMap: nextBuffSources,
        item,
      })) {
        return;
      }

      const occurrence =
        nextDebuffList.filter((value) => value === debuff).length +
        nextPermDebuffs.filter((value) => value === debuff).length;
      const key = occurrence === 0 ? debuff : `${debuff}::${occurrence}`;

      nextDebuffList.push(debuff);
      nextBuffData[key] = {
        source: item.name,
        sourceItemId: item.id,
        sourceItemEntryId: item.id,
        sourceItemDefinitionId: itemDefinitionId,
        addedAt: now,
        addedBy: 'item-equip',
        noteVisible: true,
        provenance,
      };
      if (nextBuffSources[debuff] === undefined) {
        nextBuffSources[debuff] = item.name;
      }
    });

    if (buffsToAdd.length > 0) updates.buffList = nextBuffList;
    if (debuffsToAdd.length > 0) updates.debuffList = nextDebuffList;
    updates.buffData = nextBuffData;
    updates.buffSources = nextBuffSources;
  }

  if (shouldRestoreOnEquip) {
    const disabledBuffs = char.disabledBuffs || [];
    const disabledDebuffs = char.disabledDebuffs || [];

    const hiddenBuffs = char.hiddenBuffs || [];
    const hiddenDebuffs = char.hiddenDebuffs || [];

    updates.disabledBuffs = disabledBuffs.filter((entry) => shouldKeepItemEffectLifecycleKey({
      entry,
      itemEffects: item.buffs || [],
      buffData: char.buffData || {},
    }));
    updates.disabledDebuffs = disabledDebuffs.filter((entry) => shouldKeepItemEffectLifecycleKey({
      entry,
      itemEffects: item.debuffs || [],
      buffData: char.buffData || {},
    }));
    updates.hiddenBuffs = hiddenBuffs.filter((entry) => shouldKeepItemEffectLifecycleKey({
      entry,
      itemEffects: item.buffs || [],
      buffData: char.buffData || {},
    }));
    updates.hiddenDebuffs = hiddenDebuffs.filter((entry) => shouldKeepItemEffectLifecycleKey({
      entry,
      itemEffects: item.debuffs || [],
      buffData: char.buffData || {},
    }));

    if (itemRemovedBuffs.length > 0) {
      updates.removedItemBuffs = { ...removedItemBuffs };
      delete updates.removedItemBuffs[item.id];
    }
  }

  return updates;
};

const buildLegacyUnequipProvenanceUpdates = ({ char, item }) => {
  const updates = {};
  const nextBuffList = [...(char.buffList || [])];
  const nextDebuffList = [...(char.debuffList || [])];
  const nextBuffData = { ...(char.buffData || {}) };
  const nextBuffSources = { ...(char.buffSources || {}) };
  const permanentBuffs = char.permanentBuffs || [];
  const permanentDebuffs = char.permanentDebuffs || [];
  const removedBuffOccurrences = [];
  const removedDebuffOccurrences = [];

  const refreshSourceForText = (text) => {
    const stillExists = [
      ...permanentBuffs,
      ...nextBuffList,
      ...permanentDebuffs,
      ...nextDebuffList,
    ].some((entry) => entry === text);
    if (!stillExists) {
      delete nextBuffSources[text];
      return;
    }

    const firstMetadata = nextBuffData[text];
    if (firstMetadata?.source) {
      nextBuffSources[text] = firstMetadata.source;
    } else if (nextBuffSources[text] === item.name) {
      delete nextBuffSources[text];
    }
  };

  const removeItemContribution = (text, list, priorLists) => {
    const taggedKey = Object.keys(nextBuffData).find((key) => {
      const base = key.includes('::') ? key.split('::')[0] : key;
      const effectData = nextBuffData[key] || {};
      return base === text
        && !isItemAbilityEffectData(effectData)
        && legacyEffectDataMatchesItem(effectData, item);
    });

    if (taggedKey) {
      const removedOccurrence = getLegacyEffectKeyOccurrence(taggedKey);
      const totalBefore = countLegacyText(text, permanentBuffs, nextBuffList, permanentDebuffs, nextDebuffList);
      const index = findTransientIndexForGlobalOccurrence(text, list, priorLists, removedOccurrence);
      if (index !== -1) list.splice(index, 1);
      reindexLegacyEffectDataInPlace(nextBuffData, text, totalBefore, removedOccurrence);
      refreshSourceForText(text);
      return removedOccurrence;
    }

    let index = -1;
    let removedOccurrence = -1;
    for (let candidateIndex = list.length - 1; candidateIndex >= 0; candidateIndex -= 1) {
      if (list[candidateIndex] !== text) continue;
      const occurrence = countLegacyText(text, ...(priorLists || []), list.slice(0, candidateIndex));
      const key = buildLegacyEffectKeyForOccurrence(text, occurrence);
      if (isItemAbilityEffectData(nextBuffData[key] || {})) continue;
      index = candidateIndex;
      removedOccurrence = occurrence;
      break;
    }
    if (index !== -1) {
      const totalBefore = countLegacyText(text, permanentBuffs, nextBuffList, permanentDebuffs, nextDebuffList);
      list.splice(index, 1);
      reindexLegacyEffectDataInPlace(nextBuffData, text, totalBefore, removedOccurrence);
      refreshSourceForText(text);
      return removedOccurrence;
    }

    return null;
  };

  (item.buffs || []).forEach((buff) => {
    const removedOccurrence = removeItemContribution(buff, nextBuffList, [permanentBuffs]);
    if (removedOccurrence != null) {
      removedBuffOccurrences.push({ text: buff, removedOccurrence });
    }
  });

  (item.debuffs || []).forEach((debuff) => {
    const removedOccurrence = removeItemContribution(debuff, nextDebuffList, [permanentBuffs, nextBuffList, permanentDebuffs]);
    if (removedOccurrence != null) {
      removedDebuffOccurrences.push({ text: debuff, removedOccurrence });
    }
  });

  if (item.buffs?.length > 0) updates.buffList = nextBuffList;
  if (item.debuffs?.length > 0) updates.debuffList = nextDebuffList;
  updates.buffData = nextBuffData;
  updates.buffSources = nextBuffSources;

  const disabledBuffs = char.disabledBuffs || [];
  const disabledDebuffs = char.disabledDebuffs || [];
  const hiddenBuffs = char.hiddenBuffs || [];
  const hiddenDebuffs = char.hiddenDebuffs || [];
  const remappedDisabledBuffs = remapLegacyEffectKeysAfterOccurrenceRemovals(
    disabledBuffs,
    removedBuffOccurrences,
  );
  const remappedDisabledDebuffs = remapLegacyEffectKeysAfterOccurrenceRemovals(
    disabledDebuffs,
    removedDebuffOccurrences,
  );
  const remappedHiddenBuffs = remapLegacyEffectKeysAfterOccurrenceRemovals(
    hiddenBuffs,
    removedBuffOccurrences,
  );
  const remappedHiddenDebuffs = remapLegacyEffectKeysAfterOccurrenceRemovals(
    hiddenDebuffs,
    removedDebuffOccurrences,
  );
  updates.disabledBuffs = remappedDisabledBuffs.filter((entry) => shouldKeepItemEffectLifecycleKey({
    entry,
    itemEffects: item.buffs || [],
    buffData: nextBuffData,
  }));
  updates.disabledDebuffs = remappedDisabledDebuffs.filter((entry) => shouldKeepItemEffectLifecycleKey({
    entry,
    itemEffects: item.debuffs || [],
    buffData: nextBuffData,
  }));
  updates.hiddenBuffs = remappedHiddenBuffs.filter((entry) => shouldKeepItemEffectLifecycleKey({
    entry,
    itemEffects: item.buffs || [],
    buffData: nextBuffData,
  }));
  updates.hiddenDebuffs = remappedHiddenDebuffs.filter((entry) => shouldKeepItemEffectLifecycleKey({
    entry,
    itemEffects: item.debuffs || [],
    buffData: nextBuffData,
  }));

  return updates;
};

const buildConsumedInventoryState = ({ inventory, item }) => {
  const rows = inventory || [];
  const isResettable = !!(item.uses?.resetType && item.uses.resetType !== 'none');

  if (item.uses && item.uses.current > 0) {
    const newUses = item.uses.current - 1;

    if (newUses <= 0 && item.quantity <= 1) {
      if (isResettable) {
        return rows.map((entry) =>
          entry.id === item.id
            ? { ...entry, uses: { ...entry.uses, current: 0 } }
            : entry,
        );
      }
      return rows.filter((entry) => entry.id !== item.id);
    }

    if (newUses <= 0) {
      return rows.map((entry) =>
        entry.id === item.id
          ? {
              ...entry,
              quantity: entry.quantity - 1,
              uses: { ...entry.uses, current: entry.uses.max },
            }
          : entry,
      );
    }

    return rows.map((entry) =>
      entry.id === item.id
        ? { ...entry, uses: { ...entry.uses, current: newUses } }
        : entry,
    );
  }

  if (item.quantity > 1) {
    return rows.map((entry) =>
      entry.id === item.id
        ? { ...entry, quantity: entry.quantity - 1 }
        : entry,
    );
  }

  if (!isResettable) {
    return rows.filter((entry) => entry.id !== item.id);
  }

  return undefined;
};

const hasDepletedLegacyItemUses = (item = {}) => {
  if (!item.uses || typeof item.uses !== 'object') return false;
  const current = item.uses.current ?? item.uses.cur;
  const currentNumber = Number(current);
  return Number.isFinite(currentNumber) && currentNumber <= 0;
};

export const rollLegacyDiceExpression = (notation, random = Math.random) => {
  if (!notation) return { total: 0, rolls: [], modifier: 0, formula: notation };

  const cleanNotation = String(notation).toLowerCase().replace(/\s/g, '');
  const parts = cleanNotation.split(/([+-])/);
  let total = 0;
  const allRolls = [];
  let modifier = 0;
  let currentSign = 1;

  parts.forEach((part) => {
    if (part === '+') {
      currentSign = 1;
      return;
    }
    if (part === '-') {
      currentSign = -1;
      return;
    }
    if (!part) return;

    const diceMatch = part.match(/^(\d+)?d(\d+)$/);
    if (diceMatch) {
      const numDice = parseInt(diceMatch[1], 10) || 1;
      const dieSize = parseInt(diceMatch[2], 10);
      const rolls = [];
      for (let i = 0; i < numDice; i += 1) {
        const roll = Math.floor(random() * dieSize) + 1;
        rolls.push(roll);
        total += roll * currentSign;
      }
      allRolls.push({ dice: `${numDice}d${dieSize}`, rolls, sign: currentSign });
      return;
    }

    if (part === 'mod') {
      modifier = currentSign;
      return;
    }

    const num = parseInt(part, 10);
    if (!Number.isNaN(num)) {
      total += num * currentSign;
      modifier = num * currentSign;
    }
  });

  return { total, rolls: allRolls, modifier, formula: notation };
};

export const parseLegacyConsumableResourceEffect = (effect, { random = Math.random } = {}) => {
  if (!effect || typeof effect !== 'string') return null;
  const trimmed = effect.trim();
  const match = trimmed.match(/^([+-]?)\s*(\d*d\d+(?:\s*[+-]\s*\d+)?|\d+)\s*(hp|mana|dura)\b/i);
  if (!match) return null;

  const sign = match[1] === '-' ? -1 : 1;
  const expr = match[2].replace(/\s+/g, '');
  const resource = match[3].toLowerCase();
  if (/^\d+$/.test(expr)) {
    const amount = parseInt(expr, 10) * sign;
    return {
      raw: trimmed,
      resource,
      amount,
      hadDice: false,
      breakdown: `${amount >= 0 ? '+' : ''}${amount} ${resource.toUpperCase()}`,
    };
  }

  const result = rollLegacyDiceExpression(expr, random);
  const total = result.total * sign;
  const dieParts = result.rolls.map((roll) => `[${roll.rolls.join(', ')}]`);
  const flatPart = expr.match(/[+-]\d+$/);

  return {
    raw: trimmed,
    resource,
    amount: total,
    hadDice: true,
    breakdown: `${expr}: ${dieParts.join(' + ')}${flatPart ? ` ${flatPart[0]}` : ''} = ${total >= 0 ? '+' : ''}${total} ${resource.toUpperCase()}`,
  };
};

export const parseLegacyAbilityEffectLine = (effect, { random = Math.random } = {}) => {
  if (!effect || typeof effect !== 'string') return null;
  const trimmed = effect.trim();
  const match = trimmed.match(/^([+-]?)\s*(\d*d\d+(?:\s*[+-]\s*\d+)?)\s*(.*)$/i);
  if (!match) return null;

  const expr = match[2].replace(/\s+/g, '');
  const label = match[3].trim();
  const result = rollLegacyDiceExpression(expr, random);
  const sign = match[1] === '-' ? -1 : 1;
  const total = result.total * sign;
  const dieParts = result.rolls.map((roll) => `[${roll.rolls.join(', ')}]`);
  const flatPart = expr.match(/[+-]\d+$/);

  return {
    hadDice: true,
    breakdown: `${expr}${label ? ` ${label}` : ''}: ${dieParts.join(' + ')}${flatPart ? ` ${flatPart[0]}` : ''} = ${total >= 0 ? '+' : ''}${total}${label ? ` ${label}` : ''}`,
  };
};

const normalizeLegacyAbilityEffects = (effects) => {
  if (Array.isArray(effects)) {
    return effects
      .map((effect) => String(effect || '').trim())
      .filter(Boolean);
  }

  if (typeof effects === 'string' && effects.trim()) {
    return effects
      .split(',')
      .map((effect) => effect.trim())
      .filter(Boolean);
  }

  return [];
};

const defaultLegacyAbilityIdFactory = (abilityId) => (
  `${abilityId || 'ability'}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
);

const normalizeLegacyAbilityUses = (uses) => {
  if (!uses || typeof uses !== 'object') return null;

  return {
    current: uses.current ?? uses.cur ?? uses.max ?? 1,
    max: uses.max ?? 1,
    resetType: uses.resetType || uses.reset || 'none',
  };
};

export const getLegacyInventoryAbilityName = (ability) => {
  if (typeof ability === 'string') return ability.trim();
  if (!ability || typeof ability !== 'object') return '';
  return String(ability.name || '').trim();
};

export const normalizeLegacyInventoryAbility = (
  ability,
  { abilityIdFactory = defaultLegacyAbilityIdFactory, preserveId = true } = {},
) => {
  if (typeof ability === 'string') {
    const name = ability.trim();
    if (!name) return null;

    return {
      id: abilityIdFactory(null),
      name,
      description: '',
      effects: [],
      uses: null,
    };
  }

  if (!ability || typeof ability !== 'object') {
    return null;
  }

  const name = getLegacyInventoryAbilityName(ability) || 'Ability';

  return {
    ...ability,
    id: preserveId ? (ability.id || abilityIdFactory(name)) : abilityIdFactory(ability.id || name),
    name,
    description: ability.description || ability.desc || '',
    effects: normalizeLegacyAbilityEffects(ability.effects),
    uses: normalizeLegacyAbilityUses(ability.uses),
  };
};

export const consumeLegacyInventoryItemRuntime = ({
  char,
  inventory,
  item,
  parseEffect,
  getEffectiveMaxHp,
  durabilityEnabled = true,
  manaEnabled = true,
  now = new Date().toISOString(),
}) => {
  if (!item || hasDepletedLegacyItemUses(item)) return null;

  const updates = {};
  const receiptLines = [];
  let hpRestore = 0;
  let manaRestore = 0;
  let duraRestore = 0;
  const tempBuffs = [];
  const tempDebuffs = [];
  const effects = Array.isArray(item?.onUseEffects) ? item.onUseEffects : [];
  const canApplyDurabilityEffects = durabilityEnabled !== false;
  const canApplyManaEffects = manaEnabled !== false;
  const effectiveMaxHp =
    typeof getEffectiveMaxHp === 'function'
      ? getEffectiveMaxHp(char)
      : (char.maxHp || 1);

  effects.forEach((effect) => {
    const parsed = typeof parseEffect === 'function' ? parseEffect(effect) : null;
    if (parsed) {
      if (parsed.resource === 'hp') hpRestore += parsed.amount;
      else if (parsed.resource === 'mana') {
        if (canApplyManaEffects) {
          manaRestore += parsed.amount;
        } else {
          return;
        }
      }
      else if (parsed.resource === 'dura') {
        if (canApplyDurabilityEffects) {
          duraRestore += parsed.amount;
        } else {
          return;
        }
      }

      receiptLines.push(parsed);
      return;
    }

    const text = String(effect || '').trim();
    if (!text) return;

    const lower = text.toLowerCase();
    if (lower.includes('-')) tempDebuffs.push(text);
    else if (lower.includes('+')) tempBuffs.push(text);
  });

  let hpDelta = 0;
  let manaDelta = 0;
  let duraDelta = 0;

  if (hpRestore !== 0) {
    const newHp = Math.max(0, Math.min(effectiveMaxHp, (char.currentHp || 0) + hpRestore));
    hpDelta = newHp - (char.currentHp || 0);
    updates.currentHp = newHp;
  }

  if (manaRestore !== 0) {
    const newMana = Math.max(0, Math.min(char.maxMana || 10, (char.currentMana || 0) + manaRestore));
    manaDelta = newMana - (char.currentMana || 0);
    updates.currentMana = newMana;
  }

  if (duraRestore !== 0) {
    const maxDura = char.settings?.armorDurability?.maxDurability || 25;
    const currentDura = char.armorDurability ?? maxDura;
    const newDura = Math.max(0, Math.min(maxDura, currentDura + duraRestore));
    duraDelta = newDura - currentDura;
    updates.armorDurability = newDura;
  }

  const consumedBuffs = [...tempBuffs];
  const consumedDebuffs = [...tempDebuffs];

  if (item.buffsApplyOnUse) {
    if (item.buffs?.length > 0) consumedBuffs.push(...item.buffs);
    if (item.debuffs?.length > 0) consumedDebuffs.push(...item.debuffs);
  }

  Object.assign(
    updates,
    buildLegacyItemProvenanceUpdates({
      char,
      item,
      buffs: consumedBuffs,
      debuffs: consumedDebuffs,
      addedBy: 'item-consume',
      now,
    }),
  );

  const nextInventory = buildConsumedInventoryState({ inventory, item });
  if (nextInventory !== undefined) {
    updates.inventory = nextInventory;
  }

  return {
    updates,
    receiptLines,
    deltas: {
      hpDelta,
      manaDelta,
      duraDelta,
    },
  };
};

export const toggleLegacyInventoryItemEquipRuntime = ({
  char,
  inventory,
  itemId,
  now = new Date().toISOString(),
}) => {
  const item = (inventory || []).find((entry) => entry.id === itemId);
  if (!item) return null;

  const newEquipped = !item.equipped;
  const updates = {
    inventory: (inventory || []).map((entry) => (
      entry.id === itemId ? { ...entry, equipped: newEquipped } : entry
    )),
  };

  Object.assign(
    updates,
    newEquipped
      ? buildLegacyEquipProvenanceUpdates({ char, item, now })
      : buildLegacyUnequipProvenanceUpdates({ char, item }),
  );

  return {
    item,
    newEquipped,
    updates,
  };
};

export const activateLegacyInventoryAbilityRuntime = ({
  char,
  inventory,
  itemId,
  abilityId,
  resolveEffectLine,
  now = new Date().toISOString(),
}) => {
  const item = (inventory || []).find((entry) => entry.id === itemId);
  if (!item) return null;

  const ability = item.abilities?.find((entry) => entry.id === abilityId);
  if (!ability) return null;
  const abilityUsesCurrent = ability.uses
    ? Number(ability.uses.current ?? ability.uses.cur ?? ability.uses.max ?? 0)
    : null;
  if (ability.uses && Number.isFinite(abilityUsesCurrent) && abilityUsesCurrent <= 0) return null;

  const receiptLines = [];
  const statusBuffsToAdd = [];
  const effectTexts = normalizeLegacyAbilityEffects(ability.effects);

  effectTexts.forEach((text) => {
    const resolved = typeof resolveEffectLine === 'function'
      ? resolveEffectLine(text)
      : null;
    const hadDice = !!resolved?.hadDice;
    const suppressesStatus = resolved?.suppressesStatus === true;
    const breakdown = resolved?.breakdown || text;

    receiptLines.push({ raw: text, ...(resolved || {}), hadDice, breakdown });
    if (!hadDice && !suppressesStatus) {
      statusBuffsToAdd.push(text);
    }
  });

  const updates = {};
  if (ability.uses) {
    updates.inventory = (inventory || []).map((entry) => (
      entry.id === itemId
        ? {
            ...entry,
            abilities: (entry.abilities || []).map((candidate) => (
              candidate.id === abilityId
                ? (() => {
                    const current = Number(candidate.uses?.current ?? candidate.uses?.cur ?? candidate.uses?.max ?? 0);
                    const nextCurrent = Math.max(0, (Number.isFinite(current) ? current : 0) - 1);
                    const nextUses = {
                      ...candidate.uses,
                      current: nextCurrent,
                    };
                    if (Object.prototype.hasOwnProperty.call(nextUses, 'cur')) {
                      nextUses.cur = nextCurrent;
                    }
                    return {
                      ...candidate,
                      uses: nextUses,
                    };
                  })()
                : candidate
            )),
          }
        : entry
    ));
  }

  if (statusBuffsToAdd.length > 0) {
    Object.assign(
      updates,
      buildLegacyItemProvenanceUpdates({
        char,
        item,
        buffs: statusBuffsToAdd,
        debuffs: [],
        addedBy: 'item-ability',
        now,
        ability,
        sourceLabel: `${item.name} -> ${ability.name}`,
      }),
    );
  }

  return {
    item,
    ability,
    updates,
    receiptLines,
    statusBuffsToAdd,
  };
};

export const resetLegacyInventoryUsesForRest = (inventory, restType) => {
  const normalizedRestType = restType === 'long' || restType === 'longRest' ? 'long' : 'short';
  const resetsOn = normalizedRestType === 'long' ? ['shortRest', 'longRest'] : ['shortRest'];
  const getResetType = (uses) => uses?.resetType || uses?.reset || 'none';
  const resetUses = (uses) => {
    const nextUses = { ...uses, current: uses.max };
    if (Object.prototype.hasOwnProperty.call(nextUses, 'cur')) {
      nextUses.cur = uses.max;
    }
    return nextUses;
  };

  return (inventory || []).map((item) => {
    let updatedItem = { ...item };

    if (item.uses && resetsOn.includes(getResetType(item.uses))) {
      updatedItem.uses = resetUses(item.uses);
    }

    if (item.abilities?.length > 0) {
      updatedItem.abilities = item.abilities.map((ability) => {
        if (ability.uses && resetsOn.includes(getResetType(ability.uses))) {
          return { ...ability, uses: resetUses(ability.uses) };
        }
        return ability;
      });
    }

    return updatedItem;
  });
};
